import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Grid, Card, CardContent, Button, Breadcrumbs, Link,
  TableSortLabel, TextField, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MapIcon from '@mui/icons-material/Map';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import PercentIcon from '@mui/icons-material/Percent';
import CancelIcon from '@mui/icons-material/Cancel';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useDarkMode } from './App';

const bermejon = "#AF272F";

const formatNumber = (n) => {
  if (n === null || n === undefined) return '0';
  return Number(n).toLocaleString('es-MX');
};

function exportCSV(rows, columns, labels, filename) {
  const header = labels.join(",");
  const csv = [header, ...rows.map(r => columns.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function AnimatedNumber({ value, duration = 600 }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  
  useEffect(() => {
    const from = prevRef.current;
    const to = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, '')) || 0;
    if (from === to) { setDisplay(value); return; }
    const start = performance.now();
    const numFrom = typeof from === 'number' ? from : parseFloat(String(from).replace(/[^0-9.-]/g, '')) || 0;
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      const current = Math.round(numFrom + (to - numFrom) * eased);
      setDisplay(current.toLocaleString('es-MX'));
      if (progress < 1) requestAnimationFrame(animate);
      else { setDisplay(formatNumber(to)); prevRef.current = to; }
    };
    requestAnimationFrame(animate);
    return () => { prevRef.current = to; };
  }, [value, duration]);
  
  return <>{typeof value === 'string' && value.includes('%') ? display + ' %' : display}</>;
}

function KPICard({ title, value, color, bgcolor, icon }) {
  const { darkMode } = useDarkMode();
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) || 0 : value;
  const isPercent = typeof value === 'string' && value.includes('%');
  return (
    <Card sx={{ 
      height: '100%', 
      bgcolor: darkMode ? '#1e1e1e' : '#fff', 
      minWidth: 160,
      borderLeft: `4px solid ${bgcolor || color || '#ccc'}`,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
    }}>
      <CardContent sx={{ textAlign: 'center', py: 2.5, px: 2 }}>
        <Box sx={{ 
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 40, height: 40, borderRadius: '50%', 
          bgcolor: `${bgcolor || color || '#ccc'}20`, mb: 1
        }}>
          {icon && React.cloneElement(icon, { sx: { color: bgcolor || color || '#666', fontSize: 22 } })}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: color || 'text.primary', fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2rem' } }}>
          {isPercent ? <><AnimatedNumber value={numericValue} /> %</> : <AnimatedNumber value={numericValue} />}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function ReportesComites({ token }) {
  const { darkMode } = useDarkMode();
  const [resumen, setResumen] = useState(null);
  const [dataEntidad, setDataEntidad] = useState([]);
  const [dataDistrito, setDataDistrito] = useState([]);
  const [dataSeccion, setDataSeccion] = useState([]);
  const [fechas, setFechas] = useState([]);
  const [fechaFiltro, setFechaFiltro] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Navegación por niveles
  const [nivel, setNivel] = useState('entidad'); // entidad | distrito | seccion
  const [entidadSel, setEntidadSel] = useState(null);
  const [distritoSel, setDistritoSel] = useState(null);
  const [modo, setModo] = useState('territorio'); // territorio | distrital

  // Ordenamiento
  const [orderBy, setOrderBy] = useState('');
  const [orderDir, setOrderDir] = useState('asc');

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrderDir('asc');
    }
  };

  const sortData = (data) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let va = a[orderBy], vb = b[orderBy];
      if (va === null || va === undefined) va = '';
      if (vb === null || vb === undefined) vb = '';
      if (typeof va === 'number' && typeof vb === 'number') {
        return orderDir === 'asc' ? va - vb : vb - va;
      }
      return orderDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  };

  const headerSx = { fontWeight: 700, color: '#fff', bgcolor: bermejon };

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const buildFechaParams = (fi, ff) => {
    let p = '';
    if (fi) p += `${p ? '&' : '?'}fecha_inicio=${fi}`;
    if (ff) p += `${p ? '&' : '?'}fecha_fin=${ff}`;
    return p;
  };

  const fetchResumenYEntidad = useCallback(async (fi, ff) => {
    setLoading(true);
    setError('');
    try {
      const params = buildFechaParams(fi, ff);
      const [resRes, entRes] = await Promise.all([
        fetch(`/api/comites/resumen${params}`, { headers }),
        fetch(`/api/comites/por-entidad${params}`, { headers })
      ]);
      if (!resRes.ok || !entRes.ok) throw new Error('Error cargando datos');
      const resData = await resRes.json();
      const entData = await entRes.json();
      setResumen(resData.data);
      setDataEntidad(entData.data || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [token]);

  const fetchDistrito = useCallback(async (entidad, fi, ff) => {
    setLoading(true);
    setError('');
    try {
      let params = entidad ? `?entidad=${entidad}` : '?';
      if (fi) params += `${params.length > 1 ? '&' : ''}fecha_inicio=${fi}`;
      if (ff) params += `&fecha_fin=${ff}`;
      const res = await fetch(`/api/comites/por-distrito${params}`, { headers });
      if (!res.ok) throw new Error('Error cargando distritos');
      const data = await res.json();
      setDataDistrito(data.data || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [token]);

  const fetchSeccion = useCallback(async (entidad, distrito, fi, ff) => {
    setLoading(true);
    setError('');
    try {
      let params = `?entidad=${entidad}&distrito=${distrito}`;
      if (fi) params += `&fecha_inicio=${fi}`;
      if (ff) params += `&fecha_fin=${ff}`;
      const res = await fetch(`/api/comites/por-seccion${params}`, { headers });
      if (!res.ok) throw new Error('Error cargando secciones');
      const data = await res.json();
      setDataSeccion(data.data || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetch('/api/comites/fechas', { headers })
      .then(r => r.json())
      .then(d => setFechas(d.data || []))
      .catch(() => {});
    fetchResumenYEntidad('', '');
  }, [fetchResumenYEntidad]);

  const handleFechaChange = (e) => {
    const f = e.target.value;
    setFechaFiltro(f);
    setFechaInicio(f);
    setFechaFin(f);
    setNivel('entidad');
    setEntidadSel(null);
    fetchResumenYEntidad(f, f);
  };

  const handleFechaRango = () => {
    setFechaFiltro('');
    setNivel('entidad');
    setEntidadSel(null);
    fetchResumenYEntidad(fechaInicio, fechaFin);
  };

  const handleEntidadClick = (row) => {
    setEntidadSel(row);
    setNivel('distrito');
    fetchDistrito(row.entidad, fechaInicio, fechaFin);
  };

  const handleDistritoClick = (row) => {
    if (modo === 'distrital' && row.entidad) {
      setEntidadSel({ entidad: row.entidad, estado: row.entidad_nombre });
    }
    setDistritoSel(row);
    setNivel('seccion');
    const ent = row.entidad || entidadSel?.entidad;
    fetchSeccion(ent, row.distrito_f, fechaInicio, fechaFin);
  };

  const handleVolverEntidad = () => {
    if (modo === 'distrital') {
      // En modo distrital, volver siempre va a todos los distritos
      setNivel('distrito');
      setEntidadSel(null);
      setDistritoSel(null);
      setDataSeccion([]);
      fetchDistrito(null, fechaInicio, fechaFin);
    } else {
      setNivel('entidad');
      setEntidadSel(null);
      setDistritoSel(null);
      setDataDistrito([]);
      setDataSeccion([]);
    }
  };

  const handleVolverDistrito = () => {
    if (modo === 'distrital') {
      // Desde sección en modo distrital, volver a todos los distritos
      setNivel('distrito');
      setEntidadSel(null);
      setDistritoSel(null);
      setDataSeccion([]);
      fetchDistrito(null, fechaInicio, fechaFin);
    } else {
      // Desde sección en modo territorio, volver a distritos de la entidad
      setNivel('distrito');
      setDistritoSel(null);
      setDataSeccion([]);
    }
  };

  if (loading && !resumen) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ width: '100%', mx: 'auto', px: { xs: 1, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column' }}>
      {/* Header centrado */}
      <Typography variant="h5" sx={{ fontWeight: 700, color: bermejon, textAlign: 'center', mb: 3 }}>
        COMITÉS SECCIONALES 2025
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPIs - contextuales según nivel */}
      {resumen && (() => {
        // Calcular KPIs según el nivel de navegación
        let kpis;
        if (nivel === 'entidad' && modo === 'territorio') {
          kpis = resumen;
        } else if (nivel === 'distrito') {
          // Sumar desde dataDistrito o desde entidadSel en dataEntidad
          if (modo === 'distrital') {
            kpis = {
              total_asambleas: dataDistrito.reduce((s, r) => s + (r.asambleas || 0), 0),
              con_acta_asamblea: dataDistrito.reduce((s, r) => s + (r.acta_asamblea || 0), 0),
              asambleas_canceladas: dataDistrito.reduce((s, r) => s + (r.asambleas_canceladas || 0), 0),
              asistencia_registrada: dataDistrito.reduce((s, r) => s + (r.asistencias || 0), 0),
              con_foto_asamblea: dataDistrito.reduce((s, r) => s + (r.foto_asamblea || 0), 0),
              con_evidencia_votacion: dataDistrito.reduce((s, r) => s + (r.con_evidencia_votacion || 0), 0),
              con_foto_comite: dataDistrito.reduce((s, r) => s + (r.foto_comite || 0), 0),
            };
          } else if (entidadSel) {
            kpis = {
              total_asambleas: entidadSel.asambleas || 0,
              con_acta_asamblea: entidadSel.acta_asamblea || 0,
              asambleas_canceladas: entidadSel.asambleas_canceladas || 0,
              asistencia_registrada: entidadSel.asistencias || 0,
              con_foto_asamblea: entidadSel.foto_asamblea || 0,
              con_evidencia_votacion: entidadSel.con_evidencia_votacion || 0,
              con_foto_comite: entidadSel.foto_comite || 0,
            };
          } else { kpis = resumen; }
        } else if (nivel === 'seccion' && distritoSel) {
          kpis = {
            total_asambleas: distritoSel.asambleas || 0,
            con_acta_asamblea: distritoSel.acta_asamblea || 0,
            asambleas_canceladas: distritoSel.asambleas_canceladas || 0,
            asistencia_registrada: distritoSel.asistencias || 0,
            con_foto_asamblea: distritoSel.foto_asamblea || 0,
            con_evidencia_votacion: distritoSel.con_evidencia_votacion || 0,
            con_foto_comite: distritoSel.foto_comite || 0,
          };
        } else { kpis = resumen; }
        const total = kpis.total_asambleas || 0;
        const pctActa = total > 0 ? (((kpis.con_acta_asamblea || 0) / total) * 100).toFixed(2) : 0;
        return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
          <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 1100, mb: 2 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <KPICard title="Total de Asambleas" value={kpis.total_asambleas || 0} bgcolor="#1565c0" icon={<EventIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <KPICard title="Con acta de asamblea" value={kpis.con_acta_asamblea || 0} bgcolor="#2e7d32" icon={<DescriptionIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <KPICard title="% Con Acta" value={`${pctActa} %`} color={bermejon} bgcolor={bermejon} icon={<PercentIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <KPICard title="Asambleas Canceladas" value={kpis.asambleas_canceladas || 0} bgcolor="#c62828" icon={<CancelIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <KPICard title="Asistencia registrada" value={kpis.asistencia_registrada || 0} bgcolor="#6a1b9a" icon={<PeopleIcon />} />
            </Grid>
          </Grid>
          <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 1100 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <KPICard title="Con foto de asamblea" value={kpis.con_foto_asamblea || 0} bgcolor="#ef6c00" icon={<CameraAltIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <KPICard title="Con evidencia de votación" value={kpis.con_evidencia_votacion || 0} bgcolor="#4527a0" icon={<HowToVoteIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <KPICard title="Con foto de comité" value={kpis.con_foto_comite || 0} bgcolor="#00695c" icon={<PhotoLibraryIcon />} />
            </Grid>
          </Grid>
        </Box>
        );
      })()}

      {/* Filtros de fecha - arriba de la tabla */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Fecha específica</InputLabel>
          <Select value={fechaFiltro} onChange={handleFechaChange} label="Fecha específica">
            <MenuItem value="">Todas</MenuItem>
            {fechas.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">ó</Typography>
        <TextField
          type="date"
          size="small"
          label="Desde"
          value={fechaInicio}
          onChange={(e) => setFechaInicio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          type="date"
          size="small"
          label="Hasta"
          value={fechaFin}
          onChange={(e) => setFechaFin(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <Button variant="contained" size="small" onClick={handleFechaRango} sx={{ bgcolor: bermejon, '&:hover': { bgcolor: '#8b1a22' } }}>
          Filtrar
        </Button>
        {(fechaInicio || fechaFin || fechaFiltro) && (
          <Button size="small" onClick={() => { setFechaFiltro(''); setFechaInicio(''); setFechaFin(''); fetchResumenYEntidad('', ''); }}>
            Limpiar
          </Button>
        )}
      </Box>

      {/* Toggle modo + CSV */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" value={modo} exclusive onChange={(e, val) => {
          if (!val) return;
          setModo(val);
          setNivel(val === 'distrital' ? 'distrito' : 'entidad');
          setEntidadSel(null); setDistritoSel(null);
          if (val === 'distrital') {
            fetchDistrito(null, fechaInicio, fechaFin);
          } else {
            fetchResumenYEntidad(fechaInicio, fechaFin);
          }
        }}>
          <ToggleButton value="territorio" sx={{ textTransform: 'none', fontSize: '0.75rem', color: modo === 'territorio' ? '#fff' : bermejon, bgcolor: modo === 'territorio' ? bermejon : 'transparent', borderColor: bermejon, '&.Mui-selected': { bgcolor: bermejon, color: '#fff', '&:hover': { bgcolor: '#8c1f26' } }, '&:hover': { bgcolor: '#fce4ec' } }}>
            <AccountTreeIcon sx={{ fontSize: 18, mr: 0.5 }} />Por Entidad
          </ToggleButton>
          <ToggleButton value="distrital" sx={{ textTransform: 'none', fontSize: '0.75rem', color: modo === 'distrital' ? '#fff' : bermejon, bgcolor: modo === 'distrital' ? bermejon : 'transparent', borderColor: bermejon, '&.Mui-selected': { bgcolor: bermejon, color: '#fff', '&:hover': { bgcolor: '#8c1f26' } }, '&:hover': { bgcolor: '#fce4ec' } }}>
            <MapIcon sx={{ fontSize: 18, mr: 0.5 }} />Todos los Distritos
          </ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" startIcon={<DownloadIcon />} onClick={() => {
          const rows = nivel === 'entidad' ? dataEntidad : nivel === 'distrito' ? dataDistrito : dataSeccion;
          const cols = nivel === 'entidad' 
            ? ['estado','asambleas','afiliados','asistencias','acta_asamblea','foto_asamblea','foto_comite','con_evidencia_votacion','mentores']
            : nivel === 'distrito'
            ? ['entidad_nombre','distrito_f','distrito_nombre','asambleas','afiliados','asistencias','acta_asamblea','foto_asamblea','foto_comite','con_evidencia_votacion','mentores','asambleas_canceladas']
            : ['seccion','presidente','secretario','fecha','estatus','afiliados','asistencias','con_acta','con_foto_asamblea','con_foto_comite','con_evidencia_votacion'];
          const labels = cols.map(c => c.replace(/_/g, ' ').toUpperCase());
          exportCSV(rows, cols, labels, `comites_${nivel}_${new Date().toISOString().slice(0,10)}.csv`);
        }} sx={{ color: bermejon, ml: 'auto' }}>
          CSV
        </Button>
      </Box>

      {/* Breadcrumbs de navegación */}
      {((modo === 'territorio' && nivel !== 'entidad') || (modo === 'distrital' && nivel === 'seccion')) && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={nivel === 'seccion' ? handleVolverDistrito : handleVolverEntidad} sx={{ color: bermejon }}>
            Volver
          </Button>
          <Breadcrumbs>
            {modo === 'territorio' && (
              <Link component="button" underline="hover" onClick={handleVolverEntidad} sx={{ color: bermejon }}>
                Nacional
              </Link>
            )}
            {modo === 'distrital' && nivel === 'seccion' && (
              <Link component="button" underline="hover" onClick={handleVolverDistrito} sx={{ color: bermejon }}>
                Todos los Distritos
              </Link>
            )}
            {modo === 'territorio' && entidadSel && nivel === 'distrito' && <Typography color="text.primary">{entidadSel.estado}</Typography>}
            {modo === 'territorio' && entidadSel && nivel === 'seccion' && (
              <Link component="button" underline="hover" onClick={handleVolverDistrito} sx={{ color: bermejon }}>
                {entidadSel.estado}
              </Link>
            )}
            {nivel === 'seccion' && distritoSel && <Typography color="text.primary">Dto. {distritoSel.distrito_f} - {distritoSel.distrito_nombre}</Typography>}
          </Breadcrumbs>
        </Box>
      )}

      {/* TABLA POR ENTIDAD */}
      {nivel === 'entidad' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, textAlign: 'center', color: bermejon }}>
            Comités 2025 por Entidad
          </Typography>
          <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
            <Table stickyHeader size="small" sx={{ tableLayout: "auto", fontSize: "0.8rem" }}>
              <TableHead>
                <TableRow>
                  {[
                    { id: '_idx', label: '#', nosort: true },
                    { id: 'estado', label: 'Estado' },
                    { id: 'asambleas', label: 'Asambleas', align: 'right' },
                    { id: 'afiliados', label: 'Afiliados', align: 'right' },
                    { id: 'asistencias', label: 'Asistencias', align: 'right' },
                    { id: 'acta_asamblea', label: 'Acta Asamblea', align: 'right' },
                    { id: 'foto_asamblea', label: 'Foto Asamblea', align: 'right' },
                    { id: 'foto_comite', label: 'Foto Comité', align: 'right' },
                    { id: 'con_evidencia_votacion', label: 'Evidencia Votación', align: 'right' },
                    { id: 'mentores', label: 'Mentores', align: 'right' },
                  ].map(col => (
                    <TableCell key={col.id} align={col.align || 'left'} sx={headerSx}>
                      {col.nosort ? col.label : (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? orderDir : 'asc'}
                          onClick={() => handleSort(col.id)}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                        >
                          {col.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortData(dataEntidad).map((row, idx) => (
                  <TableRow key={row.entidad || idx} hover sx={{ cursor: 'pointer' }} onClick={() => handleEntidadClick(row)}>
                    <TableCell>{idx + 1}.</TableCell>
                    <TableCell sx={{ fontWeight: 500, color: bermejon }}>{row.estado}</TableCell>
                    <TableCell align="right">{formatNumber(row.asambleas)}</TableCell>
                    <TableCell align="right">{formatNumber(row.afiliados)}</TableCell>
                    <TableCell align="right">{formatNumber(row.asistencias)}</TableCell>
                    <TableCell align="right">{formatNumber(row.acta_asamblea)}</TableCell>
                    <TableCell align="right">{formatNumber(row.foto_asamblea)}</TableCell>
                    <TableCell align="right">{formatNumber(row.foto_comite)}</TableCell>
                    <TableCell align="right">{formatNumber(row.con_evidencia_votacion)}</TableCell>
                    <TableCell align="right">{formatNumber(row.mentores)}</TableCell>
                  </TableRow>
                ))}
                {dataEntidad.length > 0 && (
                  <TableRow sx={{ bgcolor: darkMode ? '#2a2a2a' : '#f5f5f5' }}>
                    <TableCell></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataEntidad.reduce((s, r) => s + (r.asambleas || 0), 0))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataEntidad.reduce((s, r) => s + (r.afiliados || 0), 0))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataEntidad.reduce((s, r) => s + (r.asistencias || 0), 0))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataEntidad.reduce((s, r) => s + (r.acta_asamblea || 0), 0))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataEntidad.reduce((s, r) => s + (r.foto_asamblea || 0), 0))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataEntidad.reduce((s, r) => s + (r.foto_comite || 0), 0))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataEntidad.reduce((s, r) => s + (r.con_evidencia_votacion || 0), 0))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataEntidad.reduce((s, r) => s + (r.mentores || 0), 0))}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* TABLA POR DISTRITO */}
      {nivel === 'distrito' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, textAlign: 'center', color: bermejon }}>
            {modo === 'distrital' ? 'Todos los Distritos' : `${entidadSel?.estado} — Por Distrito Federal`}
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table stickyHeader size="small" sx={{ tableLayout: "auto", fontSize: "0.8rem" }}>
                <TableHead>
                  <TableRow>
                    {[
                      ...(modo === 'distrital' ? [{ id: 'entidad_nombre', label: 'Entidad' }] : []),
                      { id: 'distrito_f', label: 'Distrito' },
                      { id: 'distrito_nombre', label: 'Nombre' },
                      { id: 'asambleas', label: 'Asambleas', align: 'right' },
                      { id: 'afiliados', label: 'Afiliados', align: 'right' },
                      { id: 'asistencias', label: 'Asistencias', align: 'right' },
                      { id: 'acta_asamblea', label: 'Acta Asamblea', align: 'right' },
                      { id: 'foto_asamblea', label: 'Foto Asamblea', align: 'right' },
                      { id: 'foto_comite', label: 'Foto Comité', align: 'right' },
                      { id: 'con_evidencia_votacion', label: 'Evidencia Votación', align: 'right' },
                      { id: 'mentores', label: 'Mentores', align: 'right' },
                      { id: 'asambleas_canceladas', label: 'Canceladas', align: 'right' },
                    ].map(col => (
                      <TableCell key={col.id} align={col.align || 'left'} sx={headerSx}>
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? orderDir : 'asc'}
                          onClick={() => handleSort(col.id)}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortData(dataDistrito).map((row, idx) => (
                    <TableRow key={row.distrito_f || idx} hover sx={{ cursor: 'pointer' }} onClick={() => handleDistritoClick(row)}>
                      {modo === 'distrital' && <TableCell>{row.entidad_nombre}</TableCell>}
                      <TableCell sx={{ fontWeight: 500 }}>{row.distrito_f}</TableCell>
                      <TableCell sx={{ color: bermejon }}>{row.distrito_nombre || ''}</TableCell>
                      <TableCell align="right">{formatNumber(row.asambleas)}</TableCell>
                      <TableCell align="right">{formatNumber(row.afiliados)}</TableCell>
                      <TableCell align="right">{formatNumber(row.asistencias)}</TableCell>
                      <TableCell align="right">{formatNumber(row.acta_asamblea)}</TableCell>
                      <TableCell align="right">{formatNumber(row.foto_asamblea)}</TableCell>
                      <TableCell align="right">{formatNumber(row.foto_comite)}</TableCell>
                      <TableCell align="right">{formatNumber(row.con_evidencia_votacion)}</TableCell>
                      <TableCell align="right">{formatNumber(row.mentores)}</TableCell>
                      <TableCell align="right">{formatNumber(row.asambleas_canceladas)}</TableCell>
                    </TableRow>
                  ))}
                  {dataDistrito.length > 0 && (
                    <TableRow sx={{ bgcolor: darkMode ? '#2a2a2a' : '#f5f5f5' }}>
                      {modo === 'distrital' && <TableCell></TableCell>}
                      <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataDistrito.reduce((s, r) => s + (r.asambleas || 0), 0))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataDistrito.reduce((s, r) => s + (r.afiliados || 0), 0))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataDistrito.reduce((s, r) => s + (r.asistencias || 0), 0))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataDistrito.reduce((s, r) => s + (r.acta_asamblea || 0), 0))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataDistrito.reduce((s, r) => s + (r.foto_asamblea || 0), 0))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataDistrito.reduce((s, r) => s + (r.foto_comite || 0), 0))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataDistrito.reduce((s, r) => s + (r.con_evidencia_votacion || 0), 0))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataDistrito.reduce((s, r) => s + (r.mentores || 0), 0))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataDistrito.reduce((s, r) => s + (r.asambleas_canceladas || 0), 0))}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* TABLA POR SECCIÓN */}
      {nivel === 'seccion' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, textAlign: 'center', color: bermejon }}>
            {entidadSel?.estado} — Dto. {distritoSel?.distrito_f} {distritoSel?.distrito_nombre} — Por Sección
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table stickyHeader size="small" sx={{ tableLayout: "auto", fontSize: "0.8rem" }}>
                <TableHead>
                  <TableRow>
                    {[
                      { id: 'seccion', label: 'Sección' },
                      { id: 'presidente', label: 'Presidente' },
                      { id: 'secretario', label: 'Secretario' },
                      { id: 'fecha', label: 'Fecha' },
                      { id: 'estatus', label: 'Estatus' },
                      { id: 'afiliados', label: 'Afiliados', align: 'right' },
                      { id: 'asistencias', label: 'Asistencias', align: 'right' },
                      { id: 'con_acta', label: 'Acta', align: 'center' },
                      { id: 'con_foto_asamblea', label: 'Foto Asamblea', align: 'center' },
                      { id: 'con_foto_comite', label: 'Foto Comité', align: 'center' },
                      { id: 'con_evidencia_votacion', label: 'Evidencia Votación', align: 'center' },
                    ].map(col => (
                      <TableCell key={col.id} align={col.align || 'left'} sx={headerSx}>
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? orderDir : 'asc'}
                          onClick={() => handleSort(col.id)}
                          sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#fff !important' } }}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortData(dataSeccion).map((row, idx) => (
                    <TableRow key={row.seccion || idx} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{row.seccion}</TableCell>
                      <TableCell>{row.presidente}</TableCell>
                      <TableCell>{row.secretario}</TableCell>
                      <TableCell>{row.fecha || ''}</TableCell>
                      <TableCell sx={{ color: row.estatus === 'CANCELADA' ? '#c62828' : row.estatus === 'REALIZADA' ? '#2e7d32' : '#ef6c00' }}>
                        {row.estatus}
                      </TableCell>
                      <TableCell align="right">{formatNumber(row.afiliados)}</TableCell>
                      <TableCell align="right">{formatNumber(row.asistencias)}</TableCell>
                      <TableCell align="center">{row.con_acta ? '✅' : '❌'}</TableCell>
                      <TableCell align="center">{row.con_foto_asamblea ? '✅' : '❌'}</TableCell>
                      <TableCell align="center">{row.con_foto_comite ? '✅' : '❌'}</TableCell>
                      <TableCell align="center">{row.con_evidencia_votacion ? '✅' : '❌'}</TableCell>
                    </TableRow>
                  ))}
                  {dataSeccion.length > 0 && (
                    <TableRow sx={{ bgcolor: darkMode ? '#2a2a2a' : '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataSeccion.reduce((s, r) => s + (r.afiliados || 0), 0))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(dataSeccion.reduce((s, r) => s + (r.asistencias || 0), 0))}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>{dataSeccion.filter(r => r.con_acta).length}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>{dataSeccion.filter(r => r.con_foto_asamblea).length}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>{dataSeccion.filter(r => r.con_foto_comite).length}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>{dataSeccion.filter(r => r.con_evidencia_votacion).length}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
}
